'use client';

import { forwardRef, type MouseEvent } from 'react';
import type { MentionEntity } from '@terra/shared-dto-chat-types';
import { renderMentionCard } from '../mentions/mention-config';
import {
  readObjectContextKind,
  resolveMentionEntityIcon,
} from '../mentions/mentionEntityIcons';
import { ContextPill, type ContextPillProps } from './ContextPill';

type MentionContextPillProps = MentionEntity & {
  onClick?: (mention: MentionEntity) => void;
  onRemove?: (event: MouseEvent<HTMLButtonElement>) => void;
  removeAriaLabel?: string;
} & Omit<
    ContextPillProps,
    | 'label'
    | 'icon'
    | 'thumbnailUrl'
    | 'onClick'
    | 'onRemove'
    | 'preview'
    | 'removeAriaLabel'
  >;

export const MentionContextPill = forwardRef<
  HTMLSpanElement,
  MentionContextPillProps
>(function MentionContextPill(
  {
    entityType,
    entityId,
    label,
    extraData,
    onClick,
    onRemove,
    removeAriaLabel,
    ...spanProps
  },
  ref,
) {
  const Icon = resolveMentionEntityIcon({
    entityType,
    objectKind: readObjectContextKind(extraData),
  });
  const preview = renderMentionCard(
    { entityType, entityId, label, extraData },
    true,
  );

  return (
    <ContextPill
      ref={ref}
      label={label}
      icon={Icon}
      preview={preview || undefined}
      removeAriaLabel={removeAriaLabel}
      onRemove={onRemove}
      onClick={
        onClick
          ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              onClick({ entityType, entityId, label, extraData });
            }
          : undefined
      }
      {...spanProps}
      tone="mention"
    />
  );
});
